import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import LogWorkPage from './pages/LogWorkPage';
import WorkLogPage from './pages/WorkLogPage';
import ReportPage from './pages/ReportPage';
import VisualsPage from './pages/VisualsPage';
import ClientsPage from './pages/ClientsPage';
import SettingsPage from './pages/SettingsPage';
import AuditPage from './pages/AuditPage';
import SharedReportPage from './pages/SharedReportPage';
import WorkReportPage from './pages/WorkReportPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceEditPage from './pages/InvoiceEditPage';
import ExpensesPage from './pages/ExpensesPage';
import ReceivablesPage from './pages/ReceivablesPage';
import AttendanceClockPage from './pages/AttendanceClockPage';
import AttendanceRegisterPage from './pages/AttendanceRegisterPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectBoardPage from './pages/ProjectBoardPage';
import MyTasksPage from './pages/MyTasksPage';
// HRMS Phase 2
import HRDashboardPage from './pages/hr/HRDashboardPage';
import EmployeesPage from './pages/hr/EmployeesPage';
import AttendancePage from './pages/hr/AttendancePage';
import LeavesPage from './pages/hr/LeavesPage';
import HolidaysPage from './pages/hr/HolidaysPage';
import SalaryPage from './pages/hr/SalaryPage';
import PayrollPage from './pages/hr/PayrollPage';
import PayslipsPage from './pages/hr/PayslipsPage';
import ContactsPage from './pages/ContactsPage';
import AssetsPage from './pages/AssetsPage';
import CatalogueSettingsPage from './pages/CatalogueSettingsPage';
import ProofImagesPage from './pages/ProofImagesPage';
import DailyBriefPage from './pages/DailyBriefPage';
import PerformancePage from './pages/PerformancePage';
import TeamTasksPage from './pages/TeamTasksPage';
import BackupPage from './pages/BackupPage';
import TimelinePage from './pages/TimelinePage';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } },
});

function ProtectedRoute({ children, ownerOnly=false, hrOnly=false, noAccountant=false }) {
  const { user, loading, isAccountant } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (ownerOnly && user.role !== 'owner') return <Navigate to="/" replace />;
  if (hrOnly && user.role !== 'owner' && user.role !== 'admin') return <Navigate to="/" replace />;
  // Accountants are redirected away from non-finance pages
  if (noAccountant && isAccountant) return <Navigate to="/invoices" replace />;
  return children;
}

function AppRoutes() {
  const { user, isAccountant } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={isAccountant ? '/invoices' : '/'} replace /> : <LoginPage />} />
      <Route path="/shared/:token" element={<SharedReportPage />} />
      <Route path="/work-report/:token" element={<WorkReportPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Work Tracker — accountants redirected away from operational pages */}
        <Route index element={isAccountant ? <Navigate to="/invoices" replace /> : <DashboardPage />} />
        <Route path="log" element={<ProtectedRoute noAccountant><LogWorkPage /></ProtectedRoute>} />
        <Route path="worklog" element={<ProtectedRoute noAccountant><WorkLogPage /></ProtectedRoute>} />
        <Route path="report" element={<ProtectedRoute ownerOnly><ReportPage /></ProtectedRoute>} />
        <Route path="visuals" element={<ProtectedRoute ownerOnly><VisualsPage /></ProtectedRoute>} />
        <Route path="clients" element={<ProtectedRoute ownerOnly><ClientsPage /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute ownerOnly><SettingsPage /></ProtectedRoute>} />
        <Route path="settings/:section" element={<ProtectedRoute ownerOnly><SettingsPage /></ProtectedRoute>} />
        <Route path="audit" element={<ProtectedRoute ownerOnly><AuditPage /></ProtectedRoute>} />
        {/* Finance — owner + accountant */}
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/new" element={<ProtectedRoute ownerOnly><InvoiceEditPage /></ProtectedRoute>} />
        <Route path="invoices/:id/edit" element={<ProtectedRoute ownerOnly><InvoiceEditPage /></ProtectedRoute>} />
        <Route path="quotations" element={<InvoicesPage defaultType="quotation" />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="receivables" element={<ReceivablesPage />} />
        <Route path="attendance-clock" element={<AttendanceClockPage />} />
        <Route path="timeline" element={<ProtectedRoute noAccountant><TimelinePage /></ProtectedRoute>} />
        <Route path="quotations/new" element={<ProtectedRoute ownerOnly><InvoiceEditPage defaultType="quotation" /></ProtectedRoute>} />
        <Route path="quotations/:id/edit" element={<ProtectedRoute ownerOnly><InvoiceEditPage defaultType="quotation" /></ProtectedRoute>} />
        {/* HRMS */}
        {/* Projects */}
        <Route path="projects" element={<ProtectedRoute noAccountant><ProjectsPage /></ProtectedRoute>} />
        <Route path="projects/:id" element={<ProtectedRoute noAccountant><ProjectBoardPage /></ProtectedRoute>} />
        <Route path="my-tasks" element={<ProtectedRoute noAccountant><MyTasksPage /></ProtectedRoute>} />
        {/* HRMS */}
        <Route path="hr/dashboard" element={<ProtectedRoute hrOnly><HRDashboardPage /></ProtectedRoute>} />
        <Route path="hr/employees" element={<ProtectedRoute hrOnly><EmployeesPage /></ProtectedRoute>} />
        <Route path="hr/attendance" element={<AttendancePage />} />
        <Route path="hr/register" element={<ProtectedRoute hrOnly><AttendanceRegisterPage /></ProtectedRoute>} />
        <Route path="hr/leaves" element={<LeavesPage />} />
        <Route path="hr/holidays" element={<HolidaysPage />} />
        <Route path="hr/salary" element={<ProtectedRoute hrOnly><SalaryPage /></ProtectedRoute>} />
        <Route path="hr/payroll" element={<ProtectedRoute hrOnly><PayrollPage /></ProtectedRoute>} />
        <Route path="hr/payslips" element={<PayslipsPage />} />
        {/* CRM & Assets */}
        <Route path="contacts" element={<ProtectedRoute ownerOnly><ContactsPage /></ProtectedRoute>} />
        <Route path="assets" element={<ProtectedRoute noAccountant><AssetsPage /></ProtectedRoute>} />
        <Route path="catalogue" element={<ProtectedRoute ownerOnly><CatalogueSettingsPage /></ProtectedRoute>} />
        <Route path="proofs" element={<ProtectedRoute ownerOnly><ProofImagesPage /></ProtectedRoute>} />
        <Route path="daily-brief" element={<ProtectedRoute ownerOnly><DailyBriefPage /></ProtectedRoute>} />
        <Route path="performance" element={<ProtectedRoute noAccountant><PerformancePage /></ProtectedRoute>} />
        <Route path="team-tasks" element={<ProtectedRoute ownerOnly><TeamTasksPage /></ProtectedRoute>} />
        <Route path="backup" element={<ProtectedRoute ownerOnly><BackupPage /></ProtectedRoute>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-right" toastOptions={{ duration: 3000 }} />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
